// Transcript generation service using OpenAI API via proxy

import { supabase } from './supabaseClient';

export const generateTranscript = async (audioFile) => {
  try {
    console.log('Generating transcript via proxy...');

    const { data: { session } } = await supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      throw new Error('You must be logged in to use this feature.');
    }

    // Convert file to base64 for sending through JSON
    const arrayBuffer = await audioFile.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    // Process in chunks to avoid stack overflow for large files
    const chunkSize = 8192; // Process 8KB at a time
    let binaryString = '';
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      // Use apply instead of spread to avoid stack overflow
      binaryString += String.fromCharCode.apply(null, Array.from(chunk));
    }
    const base64Audio = btoa(binaryString);

    // Send request to our proxy endpoint
    const response = await fetch('/api/transcribe', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        audioData: base64Audio,
        fileName: audioFile.name || 'audio.mp3',
        mimeType: audioFile.type || 'audio/mpeg',
      }),
    });

    if (!response.ok) {
      // Check if response is JSON before trying to parse
      const contentType = response.headers.get('content-type');
      let errorData;
      if (contentType && contentType.includes('application/json')) {
        errorData = await response.json();
      } else {
        // If not JSON (e.g., HTML error page), get text instead
        const errorText = await response.text();
        console.error('Non-JSON error response:', errorText.substring(0, 200));

        throw new Error(`Transcription error: ${response.status} ${response.statusText}. The server may not be configured correctly.`);
      }
      throw new Error(`Transcription error: ${errorData.error || response.statusText}`);
    }

    // Check if response is JSON before parsing
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Non-JSON response received:', text.substring(0, 200));

      throw new Error('Server returned non-JSON response. Please check the API endpoint configuration.');
    }

    const data = await response.json();
    return data.transcript || '';

  } catch (error) {
    console.error('Transcription error:', error);
    throw error;
  }
};

export const enhanceTranscript = async (transcript) => {
  try {
    console.log('Enhancing transcript:', transcript);

    const { fetchOpenAIText } = await import('./openaiClient');
    const prompt = `Please improve this transcript by:
1. Fixing obvious spelling errors
2. Adding proper punctuation and capitalization
3. Correcting grammar where it's clearly wrong
4. Do NOT change the actual words or meaning
5. Do NOT add any markdown formatting like **bold** or *italic*
6. Keep the original structure and flow
7. Return only the corrected text without any explanations

Transcript: ${transcript}`;
    
    const content = await fetchOpenAIText({
      prompt,
      temperature: 0.1, // Lower temperature for more consistent results
      maxTokens: 1000,
    });
    
    // Clean up any markdown formatting that might have been added
    let cleanedContent = (content && content.trim()) || transcript;
    
    // Remove common markdown formatting
    cleanedContent = cleanedContent
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove **bold**
      .replace(/\*(.*?)\*/g, '$1')     // Remove *italic*
      .replace(/__(.*?)__/g, '$1')    // Remove __bold__
      .replace(/_(.*?)_/g, '$1')      // Remove _italic_
      .replace(/`(.*?)`/g, '$1')      // Remove `code`
      .replace(/#{1,6}\s*/g, '')      // Remove headers
      .replace(/\n\s*\n/g, '\n')      // Remove extra line breaks
      .trim();
    
    return cleanedContent;

  } catch (error) {
    console.error('Transcript enhancement error:', error);
    return transcript; // Return original if enhancement fails
  }
};

export const getAudioFileFromUrl = async (audioUrl) => {
  try {
    const response = await fetch(audioUrl);
    const blob = await response.blob();
    
    // Create a file object from the blob
    const fileName = audioUrl.split('/').pop() || 'audio.mp3';
    const file = new File([blob], fileName, { type: blob.type });
    
    return file;
  } catch (error) {
    console.error('Error fetching audio file:', error);
    throw error;
  }
}; 